/**
 * ===========================================================================
 *  audio.js — the AUDIO system: world.audio, and the wiring to gameplay
 * ===========================================================================
 * Fully synthesised WebAudio. No asset files exist in this project and none
 * are ever fetched: every sound in the game is built from oscillators, noise
 * buffers, biquads and envelopes at the moment it is triggered.
 *
 * Boot safety is the first requirement, because every other engineer is behind
 * this file. NOTHING here creates an AudioContext until a real user gesture
 * calls `unlock()`. Before that — and in the headless capture browser, which
 * has no audio device and never receives a gesture — `play()`, `music()` and
 * the whole bus wiring still run: they resolve the sound, compute the true
 * distance gain and pan against the camera, and write the trigger into
 * `world.audio.debugLog`. That is what makes "did the right sound fire at the
 * right moment" verifiable from a capture that cannot hear anything.
 *
 * Published API (every name the stub had still works):
 *   world.audio = { play(name, opts), stop(handle|id), music(state), setBus(name, gain),
 *                   unlock(), unlocked(), ctx, listenerFrom(camera), debugLog,
 *                   log(filter), counts(), clearLog(), stats(), analyze(id, opts),
 *                   stopMusic(), suspend(), intensity, states }
 *   opts = { x, y, z, gain, rate, variation, delay, pan, seed, kind, power }
 * ===========================================================================
 */
import { ORDER } from '../core/systems.js';
import { CFG } from '../core/config.js';
import { createListener, spatial } from './graph.js';
import { DEFS, PLAYABLE, createEngine } from './engine.js';
import { MUSIC_STATES } from './music.js';

const LOG_N = 384;
const FOOT_MAT = ['footstep_grass', 'footstep_dirt', 'footstep_dirt', 'footstep_stone', 'footstep_dirt'];

/* Hearing ranges, metres. Beyond these the event is not even logged — a
 * battlefield of 600 men would otherwise put 200 footsteps a second into the
 * mixer for bodies the player cannot see. */
const R_FOOT = 18, R_VOICE = 46, R_SWING = 26, R_HIT = 58;

export function createAudio() {
  /* ---------------------------------------------------------------- state */
  let W = null;
  let actx = null, eng = null;
  let unlocked = false, failed = null;
  let autoMusic = true, curState = 'silence', wantState = 'explore';
  let acc = 0, hitsPrev = 0, killsPrev = 0, rate = 0, intensity = 0;
  let frameFoots = 0, frameVoices = 0, frameHits = 0;

  const listener = createListener();
  const _sp = { gain: 0, pan: 0, dist: 0, delay: 0 };

  /* --------------------------------------------------------- trigger log */
  const ring = new Array(LOG_N);
  for (let i = 0; i < LOG_N; i++) ring[i] = { t: 0, id: '', bus: '', gain: 0, pan: 0, dist: 0, live: 0, n: 0 };
  let ri = 0, total = 0;
  const counts = Object.create(null);
  const lastAt = Object.create(null);

  function record(id, bus, gain, pan, dist, live) {
    const r = ring[ri];
    r.t = W ? +W.time.sim.toFixed(3) : 0;
    r.id = id; r.bus = bus;
    r.gain = +gain.toFixed(4); r.pan = +pan.toFixed(3); r.dist = +dist.toFixed(2);
    r.live = live ? 1 : 0; r.n = total;
    ri = (ri + 1) % LOG_N; total++;
    counts[id] = (counts[id] || 0) + 1;
  }
  function snapshot(filter, max) {
    const out = [];
    for (let i = 0; i < LOG_N; i++) {
      const r = ring[(ri + i) % LOG_N];
      if (!r.id) continue;
      if (filter && r.id.indexOf(filter) < 0) continue;
      out.push({ t: r.t, id: r.id, bus: r.bus, gain: r.gain, pan: r.pan, dist: r.dist, live: r.live, n: r.n });
    }
    return max ? out.slice(-max) : out;
  }

  /* --------------------------------------------------------- the context */
  function ensure() {
    if (actx || failed) return actx;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) { failed = 'no AudioContext'; return null; }
      actx = new AC({ latencyHint: 'interactive' });
      eng = createEngine(actx, { seed: (W?.rng?.fork?.(0x9d17)?.next?.() * 1e9 | 0) || 0x5A17D0 });
      eng.setClock(() => (W ? W.time.sim : actx.currentTime));
      copyListener();
    } catch (e) {
      failed = String(e && e.message || e);
      actx = null; eng = null;
    }
    return actx;
  }

  function copyListener() {
    if (!eng) return;
    const l = eng.listener;
    l.x = listener.x; l.y = listener.y; l.z = listener.z;
    l.rx = listener.rx; l.ry = listener.ry; l.rz = listener.rz;
    l.fx = listener.fx; l.fy = listener.fy; l.fz = listener.fz;
  }

  /* ------------------------------------------------------------- play --- */
  /**
   * The single funnel. Resolves the id, spatialises it against the camera,
   * writes the trigger to the log, and — only if a context exists — actually
   * synthesises it. Never throws.
   */
  function play(name, opts) {
    if (!name) return null;
    const def = DEFS[name];
    if (!def) { record(name + '?', 'none', 0, 0, 0, 0); return null; }

    const gainIn = (opts && opts.gain !== undefined) ? opts.gain : 1;
    let g = gainIn, pan = (opts && opts.pan) || 0, dist = 0;
    if (opts && (opts.x !== undefined || opts.z !== undefined)) {
      spatial(listener, opts.x || 0, opts.y || 0, opts.z || 0, gainIn, _sp);
      g = _sp.gain; pan = _sp.pan; dist = _sp.dist;
      if (g <= 0.0008) return null;                       // out of earshot: not a sound
    }
    // Retrigger throttle mirrors the engine's, so the log matches what a live
    // mixer would actually have made audible.
    const now = W ? W.time.real : 0;
    const prev = lastAt[name];
    if (!(opts && opts.force) && prev !== undefined && now - prev < def.gap) return null;
    lastAt[name] = now;

    record(name, def.bus, g, pan, dist, eng ? 1 : 0);
    if (!eng || !unlocked) return null;
    try { return eng.play(name, opts); } catch (e) { pushErr(e); return null; }
  }

  function pushErr(e) {
    const s = 'audio: ' + String(e && e.message || e);
    if (window.__DW && window.__DW.errors && window.__DW.errors.length < 40) window.__DW.errors.push(s);
    else console.warn(s);
  }

  /* ------------------------------------------------------------- music -- */
  function setMusic(state, urgent) {
    if (state === 'auto') { autoMusic = true; return true; }
    if (!MUSIC_STATES.includes(state)) return false;
    autoMusic = false;
    applyMusic(state, urgent);
    return true;
  }
  function applyMusic(state, urgent) {
    if (state === curState) return;
    curState = state;
    record('music:' + state, 'music', 1, 0, 0, eng ? 1 : 0);
    if (!eng || !unlocked) return;
    try {
      eng.music.setState(state, urgent);
      if (state !== 'silence' && !eng.music.m.started) eng.music.start(actx.currentTime + 0.05);
    } catch (e) { pushErr(e); }
  }

  /* --------------------------------------------------- gameplay wiring -- */
  function entityAt(id) {
    const e = W && W.entities;
    return e && id >= 0 && id < e.alive.length && e.alive[id] ? e : null;
  }
  function nearListener(x, z, r) {
    const dx = x - listener.x, dz = z - listener.z;
    return dx * dx + dz * dz <= r * r;
  }

  function onHit(id, srcId, amt, opts) {
    const e = entityAt(id); if (!e) return;
    const x = e.x[id], y = e.y[id] + 1.15, z = e.z[id];
    if (!nearListener(x, z, R_HIT)) return;
    if (frameHits++ > 6) return;
    const heavy = amt >= 70 || (opts && (opts.launch || opts.guardBreak));
    const power = Math.min(1.6, 0.55 + amt / 90);
    if (opts && opts.guardBreak) play('guardBreak', sopt(x, y, z, power));
    else if (opts && opts.launch) { play('launcher', sopt(x, y, z, power)); play('hit_heavy', sopt(x, y, z, power)); }
    else play(heavy ? 'hit_heavy' : 'hit_light', sopt(x, y, z, power));
    voiceFor(id, e, e.hp[id] > 0 ? 'pain' : 'death');
  }
  function onKill(id, srcId) {
    const e = entityAt(id); if (!e) return;
    const x = e.x[id], z = e.z[id];
    if (!nearListener(x, z, R_VOICE)) return;
    voiceFor(id, e, 'death');
    if (srcId === W.playerId) {
      const k = (W.player?.kills | 0);
      if (k > 0 && k % 50 === 0) play('ko_chime', { gain: 0.8, pan: 0 });
    }
  }
  function onAiHit(id, srcId, amount, heavy) {
    const e = entityAt(id); if (!e) return;
    const x = e.x[id], y = e.y[id] + 1.1, z = e.z[id];
    if (!nearListener(x, z, R_HIT)) return;
    if (frameHits++ > 6) return;
    play(heavy ? 'hit_heavy' : 'hit_light', sopt(x, y, z, heavy ? 1.0 : 0.7));
    voiceFor(id, e, 'pain');
  }
  function onAiKill(id, srcId) {
    const e = entityAt(id); if (!e) return;
    if (!nearListener(e.x[id], e.z[id], R_VOICE)) return;
    voiceFor(id, e, 'death');
  }
  function onGuarded(id, srcId, amount) {
    const e = entityAt(id); if (!e) return;
    play('guard', sopt(e.x[id], e.y[id] + 1.2, e.z[id], 1));
  }
  function onOfficerDown(id, srcId) {
    play('ko_chime', { gain: 1, pan: 0 });
    play('voice_cheer', { gain: 0.5, pan: 0.2 });
  }
  function onOfficerPhase(id, ph) {
    const e = entityAt(id); if (!e) return;
    play('war_horn', sopt(e.x[id], e.y[id] + 2, e.z[id], 0.7));
  }
  function onMusou(phase) {
    if (phase === 'start') { applyMusic('musou', true); autoMusic = false; }
    else { autoMusic = true; }
  }

  /** Animation markers: footsteps, clip-authored sfx, vocal effort. */
  function onAnimEvent(id, m) {
    const e = entityAt(id); if (!e || !m) return;
    const x = e.x[id], z = e.z[id];
    switch (m.type) {
      case 'foot': {
        if (!nearListener(x, z, R_FOOT)) return;
        if (frameFoots++ > 2) return;
        const mat = W.terrain?.materialAt?.(x, z) | 0;
        const isPlayer = id === W.playerId;
        play(FOOT_MAT[mat] || 'footstep_dirt',
          sopt(x, e.y[id] + 0.08, z, isPlayer ? 0.85 : 0.5 + (e.scale[id] || 1) * 0.12));
        if (isPlayer || e.kind[id] >= 3) play('armour', sopt(x, e.y[id] + 1.0, z, 0.35));
        break;
      }
      case 'swoosh': {
        if (!nearListener(x, z, R_SWING)) return;
        const k = e.kind[id];
        play(k >= 3 ? 'swing_heavy' : 'swing_light',
          sopt(x, e.y[id] + 1.3, z, (m.power || 0.7) * (id === W.playerId ? 1 : 0.55)));
        break;
      }
      case 'voice': {
        if (!nearListener(x, z, R_VOICE)) return;
        if (frameVoices++ > 3) return;
        voiceFor(id, e, m.tag || 'effort');
        break;
      }
      case 'sfx': {
        if (!m.name || !DEFS[m.name]) return;
        if (!nearListener(x, z, R_HIT)) return;
        play(m.name, sopt(x, e.y[id] + 0.6, z, 1));
        break;
      }
      case 'land': {
        if (!nearListener(x, z, R_FOOT)) return;
        play('bodyfall', sopt(x, e.y[id], z, 0.7));
        break;
      }
      default: break;
    }
  }

  /* Reused option bag — audio must not allocate per event either. */
  const _o = { x: 0, y: 0, z: 0, gain: 1, seed: 0, kind: 1, rate: 1 };
  function sopt(x, y, z, gain) { _o.x = x; _o.y = y; _o.z = z; _o.gain = gain; _o.seed = 0; _o.kind = 1; _o.rate = 1; return _o; }
  function voiceFor(id, e, tag) {
    const x = e.x[id], y = e.y[id] + 1.6, z = e.z[id];
    if (!nearListener(x, z, R_VOICE)) return;
    _o.x = x; _o.y = y; _o.z = z;
    _o.gain = id === W.playerId ? 1 : 0.72;
    // Voice is presentation/personality, not identity. `uid` remains reserved
    // for stale-reference guards; spawnKey reproduces a same-seed cast.
    _o.seed = e.spawnKey[id] || (id + 1);
    _o.kind = e.kind[id];
    _o.rate = 1;
    play('voice_' + tag, _o);
  }

  /* ------------------------------------------------- intensity + states -- */
  function evaluate(dt) {
    if (!W) return;
    const e = W.entities, id = W.playerId;
    const c = W.combat?.stats;
    if (c) {
      const dh = c.hits - hitsPrev, dk = c.kills - killsPrev;
      hitsPrev = c.hits; killsPrev = c.kills;
      rate = rate * 0.72 + (dh + dk * 2) / Math.max(0.05, dt) * 0.28;
    }
    let near = 0;
    if (id >= 0 && e.alive[id]) near = e.countIn(e.x[id], e.z[id], 14, 1) | 0;
    const duelling = !!(W.ai?.engaged && W.ai.engaged.dist < 14);
    const musou = W.player?.mode === 'musou';
    const t = Math.min(1, near / 16 + Math.min(0.55, rate / 14));
    intensity = intensity + (t - intensity) * Math.min(1, dt * 1.6);

    let s;
    if (musou) s = 'musou';
    else if (W.mission?.state === 'victory') s = 'victory';
    else if (W.mission?.state === 'defeat') s = 'defeat';
    else if (duelling) s = 'duel';
    else if (intensity > 0.62 || near >= 11) s = 'heavy';
    else if (intensity > 0.30 || near >= 5) s = 'battle';
    else if (intensity > 0.06 || near >= 1) s = 'skirmish';
    else s = 'explore';
    wantState = s;
    if (autoMusic) applyMusic(s, s === 'musou' || s === 'victory');
    if (eng && unlocked) { try { eng.ambience.setIntensity(intensity); } catch (e2) { pushErr(e2); } }
  }

  /* ================================================== the system object == */
  return {
    name: 'audio', order: ORDER.AUDIO,

    init(w) {
      W = w;
      const api = {
        /* --- required surface ------------------------------------------- */
        play, stop,
        music(state) { return setMusic(state || 'auto', false); },
        setBus(name, gain) { busGains[name] = gain; if (eng) { try { eng.setBus(name, gain); } catch (e) { pushErr(e); } } return true; },
        unlock,
        unlocked: () => unlocked,
        get ctx() { return actx; },
        listenerFrom(camera) { listener.from(camera); copyListener(); return listener; },

        /* --- names the stub published, kept working ---------------------- */
        stopMusic(fade = 0.6) { autoMusic = false; curState = 'silence'; if (eng) { try { eng.music.stop(fade); } catch (e) { pushErr(e); } } },

        /* --- verification surface ---------------------------------------- */
        /** Rolling list of the last 384 triggers: {t,id,bus,gain,pan,dist,live}. */
        get debugLog() { return snapshot(null, 0); },
        log(filter, max) { return snapshot(filter || null, max || 0); },
        counts() { return { ...counts }; },
        clearLog() { for (const r of ring) r.id = ''; ri = 0; total = 0; for (const k of Object.keys(counts)) delete counts[k]; },
        get triggers() { return total; },
        ids: PLAYABLE,
        states: MUSIC_STATES,
        get state() { return curState; },
        get intensity() { return intensity; },
        get listener() { return listener; },
        get engine() { return eng; },
        get failed() { return failed; },
        setListener(x, y, z, yaw) { listener.set(x, y, z, yaw); copyListener(); return listener; },

        stats() {
          const s = {
            unlocked, live: !!eng, failed, ctxState: actx ? actx.state : null,
            triggers: total, state: curState, want: wantState, auto: autoMusic,
            intensity: +intensity.toFixed(3), rate: +rate.toFixed(2),
            ids: PLAYABLE.length,
          };
          if (eng) { try { Object.assign(s, { engine: eng.stats() }); } catch (e) { pushErr(e); } }
          return s;
        },

        /** Offline render + waveform statistics. See src/audio/analysis.js. */
        async analyze(id, opts) { const m = await import('./analysis.js'); return m.renderSound(id, opts); },
        async analyzeMany(ids, opts) { const m = await import('./analysis.js'); return m.renderMany(ids, opts); },
        async analyzeMusic(state, opts) { const m = await import('./analysis.js'); return m.renderMusic(state, opts); },
        async analyzeVariation(id, opts) { const m = await import('./analysis.js'); return m.renderVariation(id, opts); },

        /** Suspend/resume the device without tearing the graph down. */
        suspend() { try { actx?.suspend?.(); } catch (e) { pushErr(e); } },
        resume() { try { actx?.resume?.(); } catch (e) { pushErr(e); } },
      };
      w.audio = api;

      // --- gameplay subscriptions (all guarded; a throw here would kill boot)
      try {
        const b = w.bus;
        b.on('hit', onHit); b.on('kill', onKill);
        b.on('ai:hit', onAiHit); b.on('ai:kill', onAiKill);
        b.on('guarded', onGuarded);
        b.on('officer:down', onOfficerDown); b.on('officer:phase', onOfficerPhase);
        b.on('player:musou', onMusou);
        b.on('anim:event', onAnimEvent);
      } catch (e) { pushErr(e); }

      // --- unlock on the first real gesture. Never in headless: that browser
      //     has no audio device and no user, and a context there would only
      //     burn CPU in the middle of a capture.
      if (!CFG.debug.headless && typeof addEventListener === 'function') {
        const once = () => {
          unlock();
          removeEventListener('pointerdown', once); removeEventListener('keydown', once);
          removeEventListener('touchstart', once);
        };
        addEventListener('pointerdown', once, { passive: true });
        addEventListener('keydown', once, { passive: true });
        addEventListener('touchstart', once, { passive: true });
      }
    },

    /** Presentation-rate: listener, music grid, ambience, intensity. */
    update(w, dt, alpha, realDt) {
      frameFoots = 0; frameVoices = 0; frameHits = 0;
      listener.from(w.camera);
      if (eng) {
        copyListener();
        if (unlocked && actx && actx.state === 'running') {
          const now = actx.currentTime;
          try { eng.music.tick(now); eng.ambience.tick(now); } catch (e) { pushErr(e); }
        }
      }
      acc += realDt;
      if (acc >= 0.25) { evaluate(acc); acc = 0; }
    },

    dispose() {
      try { eng?.dispose(); actx?.close?.(); } catch { /* already closed */ }
      actx = null; eng = null; unlocked = false;
    },
  };

  /* ------------------------------------------------------------ helpers -- */
  function unlock() {
    if (unlocked) return true;
    const a = ensure();
    if (!a) return false;
    unlocked = true;
    try {
      if (a.state === 'suspended') a.resume();
      for (const k of Object.keys(busGains)) eng.setBus(k, busGains[k]);
      eng.ambience.start();
      eng.music.setState(curState === 'silence' ? wantState : curState);
      eng.music.start(a.currentTime + 0.06);
      curState = eng.music.state;
    } catch (e) { pushErr(e); }
    return true;
  }

  function stop(handle, when) {
    if (!handle) return;
    if (handle === 'all') {
      try { eng?.music.stop(0.3); eng?.ambience.stop(0.4); } catch (e) { pushErr(e); }
      return;
    }
    if (typeof handle === 'string') return;      // ids are one-shots; nothing to hold
    try { handle.stop?.(when); } catch (e) { pushErr(e); }
  }
}

/** Bus levels survive a context rebuild. */
const busGains = { master: 0.72, music: 0.20, sfx: 0.72, ambience: 0.30, voice: 0.50 };
